/**
 * Content Block Schema Definitions
 *
 * This module provides JSON schema definitions for validating structured
 * content blocks in day entries. These schemas ensure data integrity and
 * consistency across the application.
 *
 * Part of Phase 2B: Bidirectional Linking Architecture
 */

// ---------------------------------------------------------------------------
// Enum constants — single source of truth used by both JSON schemas and
// the runtime validator.  Add new values here only; both places stay in sync.
// ---------------------------------------------------------------------------

/** Valid travel mode values for travel_reference content blocks. */
export const VALID_TRAVEL_TYPES = ['flight', 'train', 'bus', 'car', 'ferry', 'cruise', 'custom', 'other'];

/** Valid context values for accommodation_reference content blocks. */
export const VALID_ACCOMMODATION_CONTEXTS = ['check-in', 'staying', 'check-out', 'single-day'];

/** Valid connection-type values for document_reference content blocks. */
export const VALID_CONNECTION_TYPES = ['primary', 'related'];

/**
 * Base content block schema with common properties
 */
export const baseContentBlockSchema = {
  type: 'object',
  required: ['type', 'id'],
  properties: {
    type: {
      type: 'string',
      enum: ['text', 'accommodation_reference', 'travel_reference', 'activity_reference', 'place_reference', 'day_reference', 'document_reference']
    },
    id: {
      type: 'string',
      pattern: '^[a-zA-Z0-9_]+$',
      minLength: 5,
      description: 'Unique identifier for the content block'
    },
    createdAt: {
      type: 'string',
      format: 'date-time',
      description: 'ISO timestamp when the content block was created'
    },
    updatedAt: {
      type: 'string',
      format: 'date-time',
      description: 'ISO timestamp when the content block was last updated'
    }
  },
  additionalProperties: false
};

/**
 * Text content block schema
 */
export const textContentBlockSchema = {
  allOf: [
    baseContentBlockSchema,
    {
      type: 'object',
      required: ['text'],
      properties: {
        type: { const: 'text' },
        text: {
          type: 'string',
          minLength: 1,
          maxLength: 5000,
          description: 'The text content of the block'
        }
      }
    }
  ]
};

/**
 * Accommodation reference content block schema
 */
export const accommodationReferenceSchema = {
  allOf: [
    baseContentBlockSchema,
    {
      type: 'object',
      required: ['accommodationId', 'displayText', 'context'],
      properties: {
        type: { const: 'accommodation_reference' },
        accommodationId: {
          type: 'string',
          minLength: 1,
          description: 'ID of the referenced accommodation'
        },
        accommodationName: {
          type: 'string',
          minLength: 1,
          maxLength: 200,
          description: 'Name of the accommodation'
        },
        placeId: {
          type: ['string', 'null'],
          description: 'ID of the associated place (optional)'
        },
        placeName: {
          type: ['string', 'null'],
          maxLength: 200,
          description: 'Name of the place/city (optional)'
        },
        context: {
          type: 'string',
          enum: VALID_ACCOMMODATION_CONTEXTS,
          description: 'Context of the accommodation reference'
        },
        displayText: {
          type: 'string',
          minLength: 1,
          maxLength: 500,
          description: 'Human-readable display text for the reference'
        }
      }
    }
  ]
};

/**
 * Travel reference content block schema (for future use)
 */
export const travelReferenceSchema = {
  allOf: [
    baseContentBlockSchema,
    {
      type: 'object',
      required: ['travelId', 'displayText'],
      properties: {
        type: { const: 'travel_reference' },
        travelId: {
          type: 'string',
          minLength: 1,
          description: 'ID of the referenced travel item'
        },
        travelType: {
          type: 'string',
          enum: VALID_TRAVEL_TYPES,
          description: 'Type of travel'
        },
        displayText: {
          type: 'string',
          minLength: 1,
          maxLength: 500,
          description: 'Human-readable display text for the reference'
        }
      }
    }
  ]
};

/**
 * Activity reference content block schema (for future use)
 */
export const activityReferenceSchema = {
  allOf: [
    baseContentBlockSchema,
    {
      type: 'object',
      required: ['activityId', 'displayText'],
      properties: {
        type: { const: 'activity_reference' },
        activityId: {
          type: 'string',
          minLength: 1,
          description: 'ID of the referenced activity'
        },
        activityName: {
          type: 'string',
          minLength: 1,
          maxLength: 200,
          description: 'Name of the activity'
        },
        displayText: {
          type: 'string',
          minLength: 1,
          maxLength: 500,
          description: 'Human-readable display text for the reference'
        }
      }
    }
  ]
};

/**
 * Place reference content block schema
 */
export const placeReferenceSchema = {
  allOf: [
    baseContentBlockSchema,
    {
      type: 'object',
      required: ['placeId', 'placeName', 'displayText'],
      properties: {
        type: { const: 'place_reference' },
        placeId: {
          type: 'string',
          minLength: 1,
          description: 'ID of the referenced place'
        },
        placeName: {
          type: 'string',
          minLength: 1,
          maxLength: 200,
          description: 'Name of the place'
        },
        context: {
          type: ['string', 'null'],
          description: 'Optional context for the place reference'
        },
        displayText: {
          type: 'string',
          minLength: 1,
          maxLength: 500,
          description: 'Human-readable display text for the reference'
        }
      }
    }
  ]
};

/**
 * Day reference content block schema
 */
export const dayReferenceSchema = {
  allOf: [
    baseContentBlockSchema,
    {
      type: 'object',
      required: ['dayId', 'dayName', 'displayText'],
      properties: {
        type: { const: 'day_reference' },
        dayId: {
          type: 'string',
          minLength: 1,
          description: 'ID of the referenced day'
        },
        dayName: {
          type: 'string',
          minLength: 1,
          maxLength: 200,
          description: 'Name of the day'
        },
        date: {
          type: ['string', 'null'],
          description: 'Date of the day (YYYY-MM-DD)'
        },
        displayText: {
          type: 'string',
          minLength: 1,
          maxLength: 500,
          description: 'Human-readable display text for the reference'
        }
      }
    }
  ]
};

/**
 * Document reference content block schema
 */
export const documentReferenceSchema = {
  allOf: [
    baseContentBlockSchema,
    {
      type: 'object',
      required: ['documentId', 'documentTitle', 'displayText'],
      properties: {
        type: { const: 'document_reference' },
        documentId: {
          type: 'string',
          minLength: 1,
          description: 'ID of the referenced document'
        },
        documentTitle: {
          type: 'string',
          minLength: 1,
          maxLength: 200,
          description: 'Title of the document'
        },
        documentType: {
          type: ['string', 'null'],
          description: 'Type of the document'
        },
        connectionType: {
          type: 'string',
          enum: VALID_CONNECTION_TYPES,
          description: 'Type of connection'
        },
        displayText: {
          type: 'string',
          minLength: 1,
          maxLength: 500,
          description: 'Human-readable display text for the reference'
        }
      }
    }
  ]
};

/**
 * Complete content block schema that validates any content block type
 */
export const contentBlockSchema = {
  oneOf: [
    textContentBlockSchema,
    accommodationReferenceSchema,
    travelReferenceSchema,
    activityReferenceSchema,
    placeReferenceSchema,
    dayReferenceSchema,
    documentReferenceSchema
  ]
};

/**
 * Schema for an array of content blocks (day content)
 */
export const dayContentSchema = {
  type: 'array',
  items: contentBlockSchema,
  maxItems: 100,
  description: 'Array of structured content blocks for a day'
};

/**
 * Validates a content block against the appropriate schema
 * @param {Object} contentBlock - The content block to validate
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export function validateContentBlockSchema(contentBlock) {
  if (!contentBlock || typeof contentBlock !== 'object') {
    return {
      isValid: false,
      errors: ['Content block must be an object']
    };
  }

  const errors = [];

  // Validate required base fields
  if (!contentBlock.type) {
    errors.push('Content block type is required');
  }

  if (!contentBlock.id) {
    errors.push('Content block ID is required');
  }

  // Validate ID format
  if (contentBlock.id && !/^[a-zA-Z0-9_]+$/.test(contentBlock.id)) {
    errors.push('Content block ID must contain only alphanumeric characters and underscores');
  }

  if (contentBlock.id && contentBlock.id.length < 5) {
    errors.push('Content block ID must be at least 5 characters long');
  }

  // Type-specific validation
  switch (contentBlock.type) {
    case 'text':
      if (!contentBlock.text || typeof contentBlock.text !== 'string' || contentBlock.text.trim() === '') {
        errors.push('Text content cannot be empty');
      }
      if (contentBlock.text && contentBlock.text.length > 5000) {
        errors.push('Text content cannot exceed 5000 characters');
      }
      break;

    case 'accommodation_reference':
      if (!contentBlock.accommodationId) {
        errors.push('Accommodation ID is required for accommodation references');
      }
      if (!contentBlock.displayText) {
        errors.push('Display text is required for accommodation references');
      }
      if (!contentBlock.context) {
        errors.push('Context is required for accommodation references');
      }
      
      if (contentBlock.context && !VALID_ACCOMMODATION_CONTEXTS.includes(contentBlock.context)) {
        errors.push(`Invalid context: ${contentBlock.context}. Must be one of: ${VALID_ACCOMMODATION_CONTEXTS.join(', ')}`);
      }

      if (contentBlock.accommodationName && contentBlock.accommodationName.length > 200) {
        errors.push('Accommodation name cannot exceed 200 characters');
      }
      if (contentBlock.placeName && contentBlock.placeName.length > 200) {
        errors.push('Place name cannot exceed 200 characters');
      }
      if (contentBlock.displayText && contentBlock.displayText.length > 500) {
        errors.push('Display text cannot exceed 500 characters');
      }
      break;

    case 'travel_reference':
      if (!contentBlock.travelId) {
        errors.push('Travel ID is required for travel references');
      }
      if (!contentBlock.displayText) {
        errors.push('Display text is required for travel references');
      }
      
      if (contentBlock.travelType && !VALID_TRAVEL_TYPES.includes(contentBlock.travelType)) {
        errors.push(`Invalid travel type: ${contentBlock.travelType}. Must be one of: ${VALID_TRAVEL_TYPES.join(', ')}`);
      }
      break;

    case 'activity_reference':
      if (!contentBlock.activityId) {
        errors.push('Activity ID is required for activity references');
      }
      if (!contentBlock.displayText) {
        errors.push('Display text is required for activity references');
      }
      if (contentBlock.activityName && contentBlock.activityName.length > 200) {
        errors.push('Activity name cannot exceed 200 characters');
      }
      break;

    case 'place_reference':
      if (!contentBlock.placeId) {
        errors.push('Place ID is required for place references');
      }
      if (!contentBlock.placeName) {
        errors.push('Place name is required for place references');
      }
      if (!contentBlock.displayText) {
        errors.push('Display text is required for place references');
      }
      if (contentBlock.placeName && contentBlock.placeName.length > 200) {
        errors.push('Place name cannot exceed 200 characters');
      }
      if (contentBlock.displayText && contentBlock.displayText.length > 500) {
        errors.push('Display text cannot exceed 500 characters');
      }
      break;

    case 'day_reference':
      if (!contentBlock.dayId) {
        errors.push('Day ID is required for day references');
      }
      if (!contentBlock.dayName) {
        errors.push('Day name is required for day references');
      }
      if (!contentBlock.displayText) {
        errors.push('Display text is required for day references');
      }
      if (contentBlock.dayName && contentBlock.dayName.length > 200) {
        errors.push('Day name cannot exceed 200 characters');
      }
      if (contentBlock.displayText && contentBlock.displayText.length > 500) {
        errors.push('Display text cannot exceed 500 characters');
      }
      break;

    case 'document_reference':
      if (!contentBlock.documentId) {
        errors.push('Document ID is required for document references');
      }
      if (!contentBlock.documentTitle) {
        errors.push('Document title is required for document references');
      }
      if (!contentBlock.displayText) {
        errors.push('Display text is required for document references');
      }
      if (contentBlock.documentTitle && contentBlock.documentTitle.length > 200) {
        errors.push('Document title cannot exceed 200 characters');
      }
      if (contentBlock.displayText && contentBlock.displayText.length > 500) {
        errors.push('Display text cannot exceed 500 characters');
      }
      if (contentBlock.connectionType && !VALID_CONNECTION_TYPES.includes(contentBlock.connectionType)) {
        errors.push(`Invalid connection type: ${contentBlock.connectionType}. Must be one of: ${VALID_CONNECTION_TYPES.join(', ')}`);
      }
      break;

    default:
      if (contentBlock.type) {
        errors.push(`Unknown content block type: ${contentBlock.type}`);
      }
      break;
  }

  // Validate timestamps if present
  if (contentBlock.createdAt && !isValidISOTimestamp(contentBlock.createdAt)) {
    errors.push('createdAt must be a valid ISO timestamp');
  }
  if (contentBlock.updatedAt && !isValidISOTimestamp(contentBlock.updatedAt)) {
    errors.push('updatedAt must be a valid ISO timestamp');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Validates an array of content blocks (day content)
 * @param {Array} contentBlocks - Array of content blocks to validate
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export function validateDayContentSchema(contentBlocks) {
  if (!Array.isArray(contentBlocks)) {
    return {
      isValid: false,
      errors: ['Day content must be an array of content blocks']
    };
  }

  if (contentBlocks.length > 100) {
    return {
      isValid: false,
      errors: ['Day content cannot exceed 100 content blocks']
    };
  }

  const errors = [];
  const seenIds = new Set();

  for (let i = 0; i < contentBlocks.length; i++) {
    const block = contentBlocks[i];
    const blockValidation = validateContentBlockSchema(block);
    
    if (!blockValidation.isValid) {
      errors.push(`Content block ${i}: ${blockValidation.errors.join(', ')}`);
    }

    // Check for duplicate IDs
    if (block.id) {
      if (seenIds.has(block.id)) {
        errors.push(`Duplicate content block ID: ${block.id}`);
      } else {
        seenIds.add(block.id);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Helper function to validate ISO timestamp strings
 * @param {string} timestamp - Timestamp to validate
 * @returns {boolean} True if valid ISO timestamp
 */
function isValidISOTimestamp(timestamp) {
  if (typeof timestamp !== 'string') {
    return false;
  }
  
  const date = new Date(timestamp);
  return date instanceof Date && !isNaN(date.getTime()) && timestamp === date.toISOString();
} 